import React from "react";
import { LineChart, MessageSquare, Users } from "lucide-react";
import { BrandDetails } from "../../../types/add-brand-types";
import useGrowth from "../../../hooks/use-growth";
import useCurrentPlan from "../../../hooks/use-current-plan";
import usePaddleCheckout from "../../../hooks/use-paddle-checkout";
import useProUpgradeModal from "../../../hooks/use-pro-upgrade-modal";
import AILoader from "../../uiComponents/AILoader";
import Shimmer from "../../shimmer/Shimmer";
import { ContentMode } from "../../../types/creator-studio";
import Monitor from "./growth/Monitor";
import Seo from "./growth/Seo";
import Leads from "./growth/Leads";
import "./growth.css";
import Toast from "../../uiComponents/Toast";

type GrowthTab = "monitor" | "seo" | "leads";

const GROWTH_PRO_BENEFITS = [
  {
    title: "Social listener",
    description:
      "Track conversations and buying signals around your brand across the channels that matter.",
    icon: LineChart,
  },
  {
    title: "Competitor reviews",
    description:
      "Spot unhappy competitor customers and see recurring complaints you can convert into positioning.",
    icon: MessageSquare,
  },
  {
    title: "Lead capture",
    description:
      "Turn those signals into lead lists you can review and act on from one workspace.",
    icon: Users,
  },
];

const Growth: React.FC<{
  brandDetails: BrandDetails;
  refreshBrandDetails?: () => void;
  initialTab?: GrowthTab;
  plannedSeoTopic?: string | null;
  plannedSeoItemId?: string | null;
  onConsumedPlannedSeoTopic?: () => void;
}> = ({
  brandDetails,
  refreshBrandDetails,
  initialTab = "seo",
  plannedSeoTopic,
  plannedSeoItemId,
  onConsumedPlannedSeoTopic,
}) => {
  const { plan, loading: loadingPlan } = useCurrentPlan(brandDetails.id);
  const { openCheckout, isOpeningCheckout, isConfigured, pricesConfigured } =
    usePaddleCheckout();
  const { openUpgradeModal, upgradeModal } = useProUpgradeModal({
    onUpgradeCheckout: openCheckout,
    isUpgradeDisabled: !isConfigured,
    isUpgradeLoading: isOpeningCheckout,
    currentPlanTier: plan?.planTier,
    pricesConfigured,
  });
  const {
    activeTab,
    loading,
    handleTabChange,
    onUpdateKeywordClick,
    onSeoItemClick,
    toast,
    setToast,
  } = useGrowth(brandDetails, initialTab);

  const isGrowthLocked =
    !loadingPlan &&
    !!plan &&
    (!plan.features.seo ||
      !plan.features.socialSignalListening ||
      !plan.features.leads);
  const showGrowthLoadingState = loadingPlan && !plan;

  return (
    <>
      <div className="growth-wrapper">
        {loading && <AILoader type={ContentMode.SEO_ARTICLE} />}
        {showGrowthLoadingState ? (
          <div className="growth-locked-state growth-locked-state--loading">
            <div className="growth-locked-state__hero">
              <Shimmer width={120} height={14} borderRadius={999} />
              <Shimmer width="48%" height={34} borderRadius={12} />
              <Shimmer width="78%" height={16} borderRadius={8} />
            </div>
            <div className="growth-locked-state__benefit-grid">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="growth-locked-state__benefit-card">
                  <Shimmer width={44} height={44} borderRadius={14} />
                  <Shimmer width="62%" height={18} borderRadius={8} />
                  <Shimmer width="100%" height={12} borderRadius={8} />
                  <Shimmer width="86%" height={12} borderRadius={8} />
                </div>
              ))}
            </div>
            <Shimmer width={172} height={46} borderRadius={999} />
          </div>
        ) : isGrowthLocked ? (
          <div className="growth-locked-state">
            <div className="growth-locked-state__hero">
              <span className="growth-locked-state__eyebrow">Impret Pro</span>
              <h2 className="growth-locked-state__title">
                Unlock Growth Engine with Pro
              </h2>
              <p className="growth-locked-state__copy">
                Monitor live market signals, track competitor review trends, and
                surface lead opportunities without leaving this brand workspace.
              </p>
            </div>
            <div className="growth-locked-state__benefit-grid">
              {GROWTH_PRO_BENEFITS.map((benefit) => {
                const Icon = benefit.icon;
                return (
                  <article
                    key={benefit.title}
                    className="growth-locked-state__benefit-card"
                  >
                    <div className="growth-locked-state__benefit-icon">
                      <Icon size={20} />
                    </div>
                    <h3 className="growth-locked-state__benefit-title">
                      {benefit.title}
                    </h3>
                    <p className="growth-locked-state__benefit-copy">
                      {benefit.description}
                    </p>
                  </article>
                );
              })}
            </div>
            <button
              type="button"
              className="growth-locked-state__cta"
              onClick={openUpgradeModal}
              disabled={!isConfigured || isOpeningCheckout}
            >
              {isOpeningCheckout ? "Opening checkout..." : "Upgrade"}
            </button>
          </div>
        ) : (
          <>

            <div className="growth-header">
              <div className="growth-title-section">
                <h1 className="growth-title">Growth Engine</h1>
                <p className="growth-subtitle">
                  {activeTab === "seo"
                    ? "Discover high-impact SEO opportunities to drive organic traffic."
                    : activeTab === "monitor"
                    ? "Monitor social signals and poach competitor reviews in real-time."
                    : "Automate your outbound prospecting and monitoring."}
                </p>
              </div>

              <div className="growth-tabs">
                <button
                  className={`growth-tab ${activeTab === "seo" ? "active" : ""}`}
                  onClick={() => handleTabChange("seo")}
                >
                  SEO
                </button>
                <button
                  className={`growth-tab ${
                    activeTab === "monitor" ? "active" : ""
                  }`}
                  onClick={() => handleTabChange("monitor")}
                >
                  Monitor
                </button>
                <button
                  className={`growth-tab ${activeTab === "leads" ? "active" : ""}`}
                  onClick={() => handleTabChange("leads")}
                >
                  Leads
                </button>
              </div>
            </div>

            <div className="growth-content">
              {activeTab === "seo" && (
                <Seo
                  brandDetails={brandDetails}
                  onSeoItemClick={onSeoItemClick}
                  plannedTopic={plannedSeoTopic}
                  plannedItemId={plannedSeoItemId}
                  onConsumedPlannedTopic={onConsumedPlannedSeoTopic}
                />
              )}
              {activeTab === "monitor" && (
                <Monitor
                  brandDetails={brandDetails}
                  onUpdateKeywordClick={onUpdateKeywordClick}
                  refreshBrandDetails={refreshBrandDetails}
                />
              )}
              {activeTab === "leads" && (
                <Leads
                  brandDetails={brandDetails}
                  refreshBrandDetails={refreshBrandDetails}
                />
              )}
            </div>
          </>
        )}
      </div>
      {toast && toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
      {upgradeModal}
    </>
  );
};

export default Growth;
